import * as Sentry from "@sentry/react";

/**
 * Unified logging utility that integrates with Sentry in production
 * and falls back to console in development.
 */
export const logger = {
  /**
   * Log an error with optional context.
   * In production, sends to Sentry. In development, logs to console.
   */
  error: (message: string, error?: Error | unknown, context?: Record<string, any>) => {
    if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
      Sentry.captureException(error instanceof Error ? error : new Error(message), {
        extra: { ...context, originalMessage: message },
        tags: { severity: 'error' }
      });
    } else {
      console.error(message, error, context);
    }
  },
  
  /**
   * Log a warning with optional context.
   * In production, sends to Sentry. In development, logs to console.
   */
  warn: (message: string, context?: Record<string, any>) => {
    if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
      Sentry.captureMessage(message, {
        level: 'warning',
        extra: context
      });
    } else {
      console.warn(message, context);
    }
  },
  
  /**
   * Log informational messages.
   * Only logs in development mode, no-op in production.
   */
  info: (message: string, context?: Record<string, any>) => {
    if (import.meta.env.DEV) {
      console.log(message, context);
    }
  },

  /**
   * Log debug messages.
   * Only logs in development mode, no-op in production.
   */
  debug: (message: string, data?: any) => {
    if (import.meta.env.DEV) {
      console.log(`[DEBUG] ${message}`, data);
    }
  }
};
